import React, { ChangeEvent } from 'react';
import { FieldInputProps, FieldMetaState } from 'react-final-form';
import DatePicker, { registerLocale } from 'react-datepicker';
import cx from 'classnames';
/* eslint-disable import/no-duplicates */
import { formatISO, isDate, isValid, parse, parseISO } from 'date-fns';
import { de, enUS, nl } from 'date-fns/locale';

import AppStorage from 'Storage/Storage';
import Language from 'enums/Language';
import Label from 'FormInputs/Label/Label';
import { translate } from 'utils/utils';
import Labels from 'enums/Labels';
import TafValidationError from '../../TafValidationError/TafValidationError';

export type Props = {
  label: Labels;
  trustedLabel?: string;
  placeholder: Labels;
  isRequired: boolean;
  wrapperClass: string;
  labelWrapperClass: string;
  inputWrapperClass: string;
  meta: FieldMetaState<string>;
  input: FieldInputProps<string>;
  onBlurHandler?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onChangeHandler?: (isoDate: string) => void;
} & Partial<DefaultProps>;

const defaultProps = {
  placeholder: '',
  label: '',
  trustedLabel: '',
  isRequired: false,
  wrapperClass: '',
  labelWrapperClass: '',
  inputWrapperClass: '',
};
type DefaultProps = Readonly<typeof defaultProps>;

class InputDate extends React.PureComponent<Props> {
  private static valueIsDate = (
    value:
      | Date
      | [Date, Date]
      | ChangeEvent<HTMLInputElement>
      | React.SyntheticEvent,
  ): value is Date => isDate(value);

  private static convertDatePickerDate = (
    dateToConvert: string,
  ): Date | null => {
    let converted: Date | null = parseISO(dateToConvert);

    if (!isValid(converted)) {
      converted = null;
    }

    return converted;
  };

  private readonly storage: AppStorage;

  private readonly isTouchScreen: boolean =
    window.matchMedia('(pointer: coarse)').matches;

  static defaultProps: DefaultProps = defaultProps;

  constructor(props: Props) {
    super(props);
    this.storage = AppStorage.getInstance();

    registerLocale(Language.English, enUS);
    registerLocale(Language.Dutch, nl);
    registerLocale(Language.German, de);
  }

  propagateChange = (isoDate: string): void => {
    const { input, onChangeHandler } = this.props;

    input.onChange(isoDate);
    if (onChangeHandler) {
      onChangeHandler(isoDate);
    }
  };

  onChangeHandler = (
    d: Date | [Date, Date] | ChangeEvent<HTMLInputElement> | null,
    event?: React.SyntheticEvent<HTMLInputElement>,
  ): void => {
    let source:
      | Date
      | [Date, Date]
      | React.SyntheticEvent<HTMLInputElement>
      | null = d;

    if (event?.currentTarget?.value?.includes('/')) {
      source = event;
    }

    const formattedDate = this.formatDate(source);

    this.propagateChange(formattedDate);
  };

  onBlurHandler = (e: React.FocusEvent<HTMLInputElement>): void => {
    const { input, onBlurHandler } = this.props;

    e.target.value = this.formatDate(e);

    input.onBlur(e);

    if (onBlurHandler) onBlurHandler(e);
  };

  onFocusHandler = (e: React.FocusEvent<HTMLInputElement>): void => {
    const { input } = this.props;

    input.onFocus(e);
  };

  // eslint-disable-next-line class-methods-use-this
  formatDate = (
    dateSource:
      | Date
      | [Date, Date]
      | ChangeEvent<HTMLInputElement>
      | React.FocusEvent<HTMLInputElement>
      | React.SyntheticEvent<HTMLInputElement>
      | null,
  ): string => {
    let result = '';

    if (!dateSource) return result;

    if (InputDate.valueIsDate(dateSource)) {
      // dateSource is a Date.
      result = formatISO(dateSource, { representation: 'date' });
    } else if ('currentTarget' in dateSource) {
      // dateSource is coming from the native date input element (value format is YYYY-MM-DD).
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateSource.currentTarget.value)) {
        result = dateSource.currentTarget.value;
      } else if (/^\d{2}-\d{2}-\d{4}$/.test(dateSource.currentTarget.value)) {
        // dateSource is coming from the DatePicker component, which is formatted to dd-MM-yyyy.
        const parsedDate = parse(
          dateSource.currentTarget.value,
          'dd-MM-yyyy',
          new Date(),
        );
        result = formatISO(parsedDate, { representation: 'date' });
      } else if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateSource.currentTarget.value)) {
        // dateSource is coming from the DatePicker component, but the user entered it with slashes instead of dashes (dd/MM/yyyy).
        const parsedDate = parse(
          dateSource.currentTarget.value,
          'dd/MM/yyyy',
          new Date(),
        );
        result = formatISO(parsedDate, { representation: 'date' });
      } else {
        // dateSource is in a different format, so we try to parse it.
        if (dateSource.currentTarget.value.length === 8) {
          // Maybe the user entered the date in the format ddMMyyyy.
          const parsedDate = parse(
            dateSource.currentTarget.value,
            'ddMMyyyy',
            new Date(),
          );
          if (isValid(parsedDate)) {
            // The date is valid, so we format it to ISO format and update the input value.
            result = formatISO(parsedDate, { representation: 'date' });
            this.propagateChange(
              formatISO(parsedDate, { representation: 'date' }),
            );
          } else {
            // The date is invalid, so we log a warning.
            console.warn(
              `Invalid date found for ${dateSource.currentTarget.value}`,
            );
          }
        } else {
          // The date is in an invalid format (maybe no padded days or months), so we log a warning.
          console.warn(
            `Invalid date or format found for ${dateSource.currentTarget.value}`,
          );
        }

        // The date is in an unknown format, so we log an error.
        console.error(
          `Format could not be determined for supplied date (${dateSource.currentTarget.value})`,
        );
      }
    }

    return result;
  };

  render(): React.ReactElement {
    const {
      trustedLabel,
      label,
      input,
      meta,
      isRequired,
      placeholder,
      wrapperClass,
      labelWrapperClass,
      inputWrapperClass,
    } = this.props;

    const startDate: Date | null = InputDate.convertDatePickerDate(input.value);
    const lang = this.storage.getLocale;

    const translatedPlaceholder = translate(
      placeholder,
      this.storage.getLocale,
    );

    const formatDate = (dateString: Date) => {
      const date = new Date(dateString);
      const options: Intl.DateTimeFormatOptions = {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      };
      return date.toLocaleDateString('en-US', options).replace(',', '');
    };

    const translatedTitle =
      translate(startDate ? Labels.SELECTED_DATE : Labels.SELECT_DATE, lang) +
      (startDate ? ' ' + formatDate(startDate) : '');

    return (
      <>
        <div className={wrapperClass}>
          <div className={labelWrapperClass}>
            <Label
              trustedLabel={trustedLabel}
              label={label}
              lang={lang}
              isRequired={isRequired}
            />
          </div>

          <div className={`date-picker ${inputWrapperClass}`}>
            {this.isTouchScreen ? (
              <div className="date-picker-small-screen">
                <input
                  {...input}
                  type="date"
                  id={input.name}
                  className={cx('date-picker-for-touchscreen', {
                    'invalid-input': (meta.error as Labels) && meta.touched,
                  })}
                  placeholder={translatedPlaceholder}
                  onFocus={this.onFocusHandler}
                  onBlur={this.onBlurHandler}
                  onChange={this.onChangeHandler}
                />
              </div>
            ) : (
              <div className="date-picker-large-screen">
                <DatePicker
                  name={input.name}
                  id={input.name}
                  placeholderText={translatedPlaceholder}
                  selected={startDate}
                  className={cx({
                    'invalid-input': (meta.error as Labels) && meta.touched,
                  })}
                  onChange={this.onChangeHandler}
                  onBlur={this.onBlurHandler}
                  onFocus={this.onFocusHandler}
                  locale={lang}
                  peekNextMonth={true}
                  showMonthDropdown={true}
                  showYearDropdown={true}
                  dropdownMode="select"
                  dateFormat="dd-MM-yyyy"
                  shouldCloseOnSelect={true}
                  title={translatedTitle}
                  customInput={
                    <input
                      className="datepicker-input"
                      placeholder={translatedPlaceholder}
                    />
                  }
                  popperPlacement="bottom"
                  popperModifiers={[
                    {
                      name: 'flip',
                      enabled: false,
                    },
                    {
                      name: 'preventOverflow',
                      enabled: false,
                    },
                  ]}
                />
              </div>
            )}
            <TafValidationError
              message={translate(meta.error as Labels, lang)}
              isShown={!!(meta.error && meta.touched)}
            />
          </div>
        </div>
      </>
    );
  }
}

InputDate.defaultProps = defaultProps;

export default InputDate;
